import { ApiError } from "../lib/api";
import { en, type MessageKey } from "./en";

/**
 * Every stable API error code the UI can show. Each code needs an `error.<code>` key in
 * en.ts and sr.ts (checked by the i18n test). Anything else falls back to `error.unknown`.
 */
export const API_ERROR_CODES = [
  "network_error",
  "bad_response",
  "unknown_error",
  "internal_error",
  "http_error",
  "validation_error",
  "not_found",
  "unauthorized",
  "forbidden",
  "csrf_origin",
  "access_required",
  "rate_limited",
  "locked",
  "photo_locked",
  "song_locked",
  "consent_required",
  "quota_exceeded",
  "too_large",
  "ext_not_allowed",
  "media_kind_mismatch",
  "checksum_mismatch",
  "size_mismatch",
  "empty_body",
  "length_required",
  "duration_required",
  "song_required",
  "region_invalid",
  "hotkey_duplicate",
  "hotkey_invalid",
  "hotkey_reserved",
  "person_duplicate",
  "person_not_found",
  "export_invalid",
  "owner_protected",
  "cannot_remove_self",
  "not_invited",
  "invite_invalid",
  "invite_email_mismatch",
  "email_unverified",
  "oauth_not_configured",
  "oauth_state_invalid",
  "dev_login_forbidden",
] as const;

export type ApiErrorCode = (typeof API_ERROR_CODES)[number];

const KNOWN_KEYS: ReadonlySet<string> = new Set(Object.keys(en));

/** Message key for an API error code, or the generic key for unknown codes. */
export function messageKeyForCode(code: string): MessageKey {
  const key = `error.${code}`;
  return KNOWN_KEYS.has(key) ? (key as MessageKey) : "error.unknown";
}

/** Message key for any thrown value: ApiError codes map to their text, the rest is generic. */
export function messageKeyForError(error: unknown): MessageKey {
  return error instanceof ApiError ? messageKeyForCode(error.code) : "error.unknown";
}
